import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, type Home as HomeData } from "./api";
import { AppSignIn } from "./pages/AppSignIn";
import { AddCar, CarDetail, Cars } from "./pages/Cars";
import { Checklist } from "./pages/Checklist";
import { Work } from "./pages/Work";
import { AddLoan, AddProperty, LoanDetailPage, Properties, PropertyDetailPage } from "./pages/Properties";
import { Home } from "./pages/Home";
import { Inbox } from "./pages/Inbox";
import { Onboarding } from "./pages/Onboarding";
import { Records } from "./pages/Records";
import { Review } from "./pages/Review";
import { SignIn } from "./pages/SignIn";
import { SessionProvider, useSession } from "./session";
import { Loading, Mark, Menu } from "./ui";

export function App() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/app-sign-in" element={<AppSignIn />} />
        <Route path="*" element={<SignedIn />} />
      </Routes>
    </SessionProvider>
  );
}

function SignedIn() {
  const { me, loading } = useSession();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/sign-in?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  if (!me.person.onboardingComplete && location.pathname !== "/welcome") return <Navigate to="/welcome" replace />;
  if (location.pathname === "/welcome") return <Onboarding />;
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/inbox/:id" element={<Review />} />
        <Route path="/records" element={<Records />} />
        <Route path="/cars" element={<Cars />} />
        <Route path="/cars/new" element={<AddCar />} />
        <Route path="/cars/:id" element={<CarDetail />} />
        <Route path="/work" element={<Work />} />
        <Route path="/documents" element={<Checklist />} />
        <Route path="/properties" element={<Properties />} />
        <Route path="/properties/new" element={<AddProperty />} />
        <Route path="/properties/:id" element={<PropertyDetailPage />} />
        <Route path="/loans/new" element={<AddLoan />} />
        <Route path="/loans/:id" element={<LoanDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { me, signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [toReview, setToReview] = useState(0);
  const [showCars, setShowCars] = useState(false);
  const [showProperties, setShowProperties] = useState(false);
  const [showWork, setShowWork] = useState(false);

  useEffect(() => {
    api.get<HomeData>("/api/home").then((h) => {
      setToReview(h.toReview);
      setShowCars(h.vehicles > 0 || h.areas.includes("work_car"));
      setShowWork(h.jobs > 0 || h.areas.includes("job") || h.areas.includes("work_from_home"));
      setShowProperties(h.properties > 0 || h.areas.includes("investment_property") || h.areas.includes("own_home"));
    }).catch(() => undefined);
  }, [location.pathname]);

  return (
    <div className="shell">
      <header className="topbar">
        <NavLink to="/" className="brand"><Mark /><span>TaxApp</span></NavLink>
        <nav className="nav" aria-label="Main">
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/inbox">To review{toReview > 0 && <span className="badge" aria-label={`${toReview} to review`}>{toReview}</span>}</NavLink>
          <NavLink to="/records">Records</NavLink>
          {showWork && <NavLink to="/work">Work</NavLink>}
          {showProperties && <NavLink to="/properties">Properties</NavLink>}
          {showCars && <NavLink to="/cars">Cars</NavLink>}
        </nav>
        <div className="spacer" />
        <Menu label={<span aria-label="Account">{me?.person.displayName.split(" ")[0] ?? "Account"} ▾</span>}>
          <button type="button" onClick={async () => { await signOut(); navigate("/sign-in"); }}>Sign out</button>
          <button type="button" onClick={async () => {
            if (!window.confirm("Sign out of TaxApp on every device, including your iPhone and this browser?")) return;
            await api.post("/auth/logout-everywhere");
            await signOut().catch(() => undefined);
            navigate("/sign-in");
          }}>Sign out everywhere</button>
        </Menu>
      </header>
      <main className="main">{children}</main>
    </div>
  );
}
